import { Vec2 } from 'ayvri-std/lib/vector';
import { defined, convertToRadian } from './helpers';
import { EventType, KeyboardEventModifier } from './InputEventHandler';
export const AggregatorType = {
    LEFT_DRAG: 0,
    RIGHT_DRAG: 1,
    MIDDLE_DRAG: 2,
    WHEEL: 3,
    PINCH: 4,
};
function getKey(type, modifier) {
    let key = type;
    if (defined(modifier)) {
        key += `+${modifier}`;
    }
    return key;
}
function clonePinchMovement(pinchMovement, result) {
    result.distance.startPosition.copy(pinchMovement.distance.startPosition);
    result.distance.endPosition.copy(pinchMovement.distance.endPosition);
    result.angleAndHeight.startPosition.copy(pinchMovement.angleAndHeight.startPosition);
    result.angleAndHeight.endPosition.copy(pinchMovement.angleAndHeight.endPosition);
}
function listenToPinch(aggregator, modifier, canvas) {
    const key = getKey(AggregatorType.PINCH, modifier);
    const update = aggregator._update;
    const isDown = aggregator._isDown;
    const eventStartPosition = aggregator._eventStartPosition;
    const pressTime = aggregator._pressTime;
    const releaseTime = aggregator._releaseTime;
    update[key] = true;
    isDown[key] = false;
    eventStartPosition[key] = new Vec2();
    let movement = aggregator._movement[key];
    if (!defined(movement)) {
        movement = {};
        aggregator._movement[key] = movement;
    }
    movement.distance = {
        startPosition: new Vec2(),
        endPosition: new Vec2(),
    };
    movement.angleAndHeight = {
        startPosition: new Vec2(),
        endPosition: new Vec2(),
    };
    movement.prevAngle = 0.0;
    aggregator._eventHandler.setInputAction(event => {
        aggregator._buttonsDown++;
        isDown[key] = true;
        pressTime[key] = new Date();
        // Compute center position and store as start point.
        Vec2.lerp(event.position1, event.position2, 0.5, eventStartPosition[key]);
    }, EventType.PINCH_START, modifier);
    aggregator._eventHandler.setInputAction(() => {
        aggregator._buttonsDown = Math.max(aggregator._buttonsDown - 1, 0);
        isDown[key] = false;
        releaseTime[key] = new Date();
    }, EventType.PINCH_END, modifier);
    aggregator._eventHandler.setInputAction(mouseMovement => {
        if (isDown[key]) {
            // Aggregate several input events into a single animation frame.
            if (!update[key]) {
                movement.distance.endPosition.copy(mouseMovement.distance.endPosition);
                movement.angleAndHeight.endPosition.copy(mouseMovement.angleAndHeight.endPosition);
            }
            else {
                clonePinchMovement(mouseMovement, movement);
                update[key] = false;
                movement.prevAngle = movement.angleAndHeight.startPosition.x;
            }
            // Make sure our aggregation of angles does not "flip" over 360 degrees.
            let angle = movement.angleAndHeight.endPosition.x;
            const prevAngle = movement.prevAngle;
            const TwoPI = Math.PI * 2;
            while (angle >= prevAngle + Math.PI) {
                angle -= TwoPI;
            }
            while (angle < prevAngle - Math.PI) {
                angle += TwoPI;
            }
            movement.angleAndHeight.endPosition.x = (-angle * canvas.clientWidth) / 12;
            movement.angleAndHeight.startPosition.x = (-prevAngle * canvas.clientWidth) / 12;
        }
    }, EventType.PINCH_MOVE, modifier);
}
function listenToWheel(aggregator, modifier) {
    const key = getKey(AggregatorType.WHEEL, modifier);
    const update = aggregator._update;
    update[key] = true;
    let movement = aggregator._movement[key];
    if (!defined(movement)) {
        movement = {};
        aggregator._movement[key] = movement;
    }
    movement.startPosition = new Vec2();
    movement.endPosition = new Vec2();
    aggregator._eventHandler.setInputAction(delta => {
        // TODO: magic numbers
        const arcLength = 15.0 * convertToRadian(delta);
        if (!update[key]) {
            movement.endPosition.y += arcLength;
        }
        else {
            movement.startPosition.init(0, 0);
            movement.endPosition.x = 0.0;
            movement.endPosition.y = arcLength;
            update[key] = false;
        }
    }, EventType.WHEEL, modifier);
}
function listenMouseButtonDownUp(aggregator, modifier, type) {
    const key = getKey(type, modifier);
    const isDown = aggregator._isDown;
    const eventStartPosition = aggregator._eventStartPosition;
    const pressTime = aggregator._pressTime;
    isDown[key] = false;
    eventStartPosition[key] = new Vec2();
    let lastMovement = aggregator._lastMovement[key];
    if (!defined(lastMovement)) {
        lastMovement = {
            startPosition: new Vec2(),
            endPosition: new Vec2(),
            valid: false,
        };
        aggregator._lastMovement[key] = lastMovement;
    }
    let down;
    let up;
    if (type === AggregatorType.LEFT_DRAG) {
        down = EventType.LEFT_DOWN;
        up = EventType.LEFT_UP;
    }
    else if (type === AggregatorType.RIGHT_DRAG) {
        down = EventType.RIGHT_DOWN;
        up = EventType.RIGHT_UP;
    }
    else if (type === AggregatorType.MIDDLE_DRAG) {
        down = EventType.MIDDLE_DOWN;
        up = EventType.MIDDLE_UP;
    }
    aggregator._eventHandler.setInputAction(event => {
        aggregator._buttonsDown++;
        lastMovement.valid = false;
        isDown[key] = true;
        pressTime[key] = new Date();
        eventStartPosition[key].copy(event.position);
    }, down, modifier);
    aggregator._eventHandler.setInputAction(() => {
        // Release the mouse button, but also any button + modifiers that might be down
        const regex = /([0-9]+)\+?/;
        const buttonKeys = Object.keys(aggregator._isDown).filter(modKey => modKey.match(regex)[1] === type.toString());
        const activeKeys = buttonKeys.filter(modKey => aggregator._isDown[modKey]);
        activeKeys.forEach(modKey => {
            aggregator._buttonsDown = Math.max(aggregator._buttonsDown - 1, 0);
            aggregator._isDown[modKey] = false;
            aggregator._releaseTime[modKey] = new Date();
        });
    }, up, modifier);
}
function cloneMouseMovement(mouseMovement, result) {
    result.startPosition.copy(mouseMovement.startPosition);
    result.endPosition.copy(mouseMovement.endPosition);
}
function listenMouseMove(aggregator, modifier) {
    const update = aggregator._update;
    const movement = aggregator._movement;
    const lastMovement = aggregator._lastMovement;
    const isDown = aggregator._isDown;
    const eventTypes = Object.keys(EventType);
    for (let i = 0; i < eventTypes.length; i++) {
        const type = EventType[eventTypes[i]];
        const key = getKey(type, modifier);
        update[key] = true;
        if (!defined(aggregator._lastMovement[key])) {
            aggregator._lastMovement[key] = {
                startPosition: new Vec2(),
                endPosition: new Vec2(),
                valid: false,
            };
        }
        if (!defined(aggregator._movement[key])) {
            aggregator._movement[key] = {
                startPosition: new Vec2(),
                endPosition: new Vec2(),
            };
        }
    }
    aggregator._eventHandler.setInputAction(mouseMovement => {
        for (let i = 0; i < eventTypes.length; i++) {
            const type = EventType[eventTypes[i]];
            const key = getKey(type, modifier);
            if (isDown[key]) {
                if (!update[key]) {
                    movement[key].endPosition.copy(mouseMovement.endPosition);
                }
                else {
                    cloneMouseMovement(movement[key], lastMovement[key]);
                    lastMovement[key].valid = true;
                    cloneMouseMovement(mouseMovement, movement[key]);
                    update[key] = false;
                }
            }
        }
        aggregator._currentMousePosition.copy(mouseMovement.endPosition);
    }, EventType.MOUSE_MOVE, modifier);
}
/**
 * Aggregates input events. For example, suppose the following inputs are received between frames:
 * left mouse button down, mouse move, mouse move, left mouse button up. These events will be aggregated into
 * one event with a start and end position of the mouse.
 */
export default class InputEventAggregator {
    constructor(canvas, eventHandler) {
        this._eventHandler = eventHandler;
        this._update = {};
        this._movement = {};
        this._lastMovement = {};
        this._isDown = {};
        this._eventStartPosition = {};
        this._pressTime = {};
        this._releaseTime = {};
        this._buttonsDown = 0;
        this._currentMousePosition = new Vec2();
        listenToWheel(this, undefined);
        listenToPinch(this, undefined, canvas);
        listenMouseButtonDownUp(this, undefined, AggregatorType.LEFT_DRAG);
        listenMouseButtonDownUp(this, undefined, AggregatorType.RIGHT_DRAG);
        listenMouseButtonDownUp(this, undefined, AggregatorType.MIDDLE_DRAG);
        listenMouseMove(this, undefined);
        const modifiers = Object.keys(KeyboardEventModifier);
        for (let i = 0; i < modifiers.length; i++) {
            const modifier = KeyboardEventModifier[modifiers[i]];
            listenToWheel(this, modifier);
            listenToPinch(this, modifier, canvas);
            listenMouseButtonDownUp(this, modifier, AggregatorType.LEFT_DRAG);
            listenMouseButtonDownUp(this, modifier, AggregatorType.RIGHT_DRAG);
            listenMouseButtonDownUp(this, modifier, AggregatorType.MIDDLE_DRAG);
            listenMouseMove(this, modifier);
        }
    }
    get currentMousePosition() {
        return this._currentMousePosition;
    }
    /** Gets whether any mouse button is down, a touch has started, or the wheel has been moved. */
    get anyButtonDown() {
        const wheelMoved = !this._update[getKey(AggregatorType.WHEEL)] ||
            !this._update[getKey(AggregatorType.WHEEL, KeyboardEventModifier.SHIFT)] ||
            !this._update[getKey(AggregatorType.WHEEL, KeyboardEventModifier.CTRL)] ||
            !this._update[getKey(AggregatorType.WHEEL, KeyboardEventModifier.ALT)];
        return this._buttonsDown > 0 || wheelMoved;
    }
    /** Gets if a mouse button down or touch has started and has been moved. */
    isMoving(type, modifier) {
        const key = getKey(type, modifier);
        return !this._update[key];
    }
    /** Gets the aggregated start and end position of the current event. */
    getMovement(type, modifier) {
        const key = getKey(type, modifier);
        const movement = this._movement[key];
        return movement;
    }
    /** Gets the start and end position of the last move event (not the aggregated event). */
    getLastMovement(type, modifier) {
        const key = getKey(type, modifier);
        const lastMovement = this._lastMovement[key];
        if (lastMovement.valid) {
            return lastMovement;
        }
        return undefined;
    }
    /** Gets whether the mouse button is down or a touch has started. */
    isButtonDown(type, modifier) {
        const key = getKey(type, modifier);
        return this._isDown[key];
    }
    /** Gets the mouse position that started the aggregation. */
    getStartMousePosition(type, modifier) {
        if (type === AggregatorType.WHEEL) {
            return this._currentMousePosition;
        }
        const key = getKey(type, modifier);
        return this._eventStartPosition[key];
    }
    /** Gets the time the button was pressed or the touch was started. */
    getButtonPressTime(type, modifier) {
        const key = getKey(type, modifier);
        return this._pressTime[key];
    }
    /** Gets the time the button was released or the touch was ended. */
    getButtonReleaseTime(type, modifier) {
        const key = getKey(type, modifier);
        return this._releaseTime[key];
    }
    /**
     * Signals that all of the events have been handled and the aggregator should be reset to handle new events.
     */
    reset() {
        const props = Object.keys(this._update);
        for (let i = 0; i < props.length; i++) {
            this._update[props[i]] = true;
        }
    }
}
